export interface RegentsTestQuestion {
  id: number;
  part: 'I' | 'II' | 'III' | 'IV';
  points: number;
  topic: string;
  standard: string;
  question: string;
  options?: string[];
  correctAnswer?: number;
  solution: string[];
  diagram?: string;
  hint?: string;
  rubric?: string[];
  commonMistakes?: string[];
}

// ─── GEOMETRY REGENTS TEST 3 — PART I (24 MCQ, 2 pts each = 48 pts) ──────────
export const test3PartI: RegentsTestQuestion[] = [
  {
    id: 1, part: 'I', points: 2,
    topic: 'Translation', standard: 'G.CO.A.2',
    question: 'Point M(−3, 7) is translated by the vector 〈4, −5〉. What are the coordinates of M\'?',
    options: ['(1, 2)', '(−7, 12)', '(1, 12)', '(7, 2)'],
    correctAnswer: 0,
    solution: [
      'Translation: (x, y) → (x + h, y + k)',
      'M(−3, 7) → M\'(−3+4, 7−5) = M\'(1, 2)',
      'Answer: (1, 2)'
    ]
  },
  {
    id: 2, part: 'I', points: 2,
    topic: 'Isosceles Triangle', standard: 'G.CO.C.10',
    question: 'In isosceles triangle ABC, AB = AC. If angle B = 72°, what is angle A?',
    options: ['36°', '72°', '54°', '108°'],
    correctAnswer: 0,
    solution: [
      'In isosceles triangle: base angles are congruent.',
      'Angle B = Angle C = 72°',
      'Sum of angles = 180°',
      'Angle A = 180° − 72° − 72° = 36°',
      'Answer: 36°'
    ]
  },
  {
    id: 3, part: 'I', points: 2,
    topic: 'Alternate Interior Angles', standard: 'G.CO.C.9',
    question: 'Lines p and q are parallel. A transversal forms angle 1 = 3x + 20 and its alternate interior angle = 5x − 10. Find x.',
    options: ['15', '5', '30', '10'],
    correctAnswer: 0,
    solution: [
      'Alternate interior angles are congruent when lines are parallel.',
      '3x + 20 = 5x − 10',
      'Subtract 3x: 20 = 2x − 10',
      'Add 10: 30 = 2x',
      'x = 15',
      'Answer: 15'
    ]
  },
  {
    id: 4, part: 'I', points: 2,
    topic: 'Pythagorean Theorem', standard: 'G.SRT.B.4',
    question: 'A right triangle has one leg of 7 and hypotenuse of 25. What is the length of the other leg?',
    options: ['24', '18', '32', '√576'],
    correctAnswer: 0,
    solution: [
      'a² + b² = c²',
      '7² + b² = 25²',
      '49 + b² = 625',
      'b² = 576',
      'b = 24',
      'Answer: 24'
    ]
  },
  {
    id: 5, part: 'I', points: 2,
    topic: 'Circle — Diameter & Chord', standard: 'G.C.A.2',
    question: 'A diameter of a circle is 18. What is the longest chord that can be drawn in the circle?',
    options: ['18', '9', '36', '27'],
    correctAnswer: 0,
    solution: [
      'The longest chord in a circle is the diameter.',
      'Diameter = 18',
      'Answer: 18'
    ]
  },
  {
    id: 6, part: 'I', points: 2,
    topic: 'Volume — Sphere', standard: 'G.GMD.A.3',
    question: 'A sphere has radius 3. What is its volume?',
    options: ['36π', '12π', '9π', '108π'],
    correctAnswer: 0,
    solution: [
      'Volume of sphere = (4/3)πr³',
      'V = (4/3)π(3)³',
      'V = (4/3)π(27) = 36π',
      'Answer: 36π'
    ]
  },
  {
    id: 7, part: 'I', points: 2,
    topic: 'Trigonometry — Cosine', standard: 'G.SRT.C.8',
    question: 'In right triangle DEF with right angle at E, cos(D) = 12/13. If DF = 39, what is DE?',
    options: ['36', '15', '12', '13'],
    correctAnswer: 0,
    solution: [
      'cos(D) = adjacent/hypotenuse = DE/DF',
      '12/13 = DE/39',
      'DE = 39 × (12/13) = 36',
      'Answer: 36'
    ]
  },
  {
    id: 8, part: 'I', points: 2,
    topic: 'Rotation', standard: 'G.CO.A.5',
    question: 'Point N(−2, 5) is rotated 90° counterclockwise about the origin. What are the coordinates of N\'?',
    options: ['(−5, −2)', '(5, 2)', '(2, −5)', '(−5, 2)'],
    correctAnswer: 0,
    solution: [
      'Rotation 90° CCW about origin: (x, y) → (−y, x)',
      'N(−2, 5) → N\'(−5, −2)',
      'Answer: (−5, −2)'
    ]
  },
  {
    id: 9, part: 'I', points: 2,
    topic: 'Rectangle Properties', standard: 'G.CO.C.11',
    question: 'In rectangle WXYZ, diagonal WY = 20. What is diagonal XZ?',
    options: ['20', '10', '40', '√400'],
    correctAnswer: 0,
    solution: [
      'In a rectangle, diagonals are congruent.',
      'XZ = WY = 20',
      'Answer: 20'
    ]
  },
  {
    id: 10, part: 'I', points: 2,
    topic: 'Dilation', standard: 'G.SRT.A.1',
    question: 'Square ABCD has side length 8. It is dilated by scale factor 3/4. What is the side length of the image?',
    options: ['6', '10.67', '32/3', '2'],
    correctAnswer: 0,
    solution: [
      'Dilation multiplies all lengths by the scale factor.',
      'New side = 8 × (3/4) = 6',
      'Answer: 6'
    ]
  },
  {
    id: 11, part: 'I', points: 2,
    topic: 'Inscribed Angle', standard: 'G.C.A.2',
    question: 'An inscribed angle measures 55°. What is the measure of its intercepted arc?',
    options: ['110°', '55°', '27.5°', '220°'],
    correctAnswer: 0,
    solution: [
      'Inscribed angle = (1/2) × intercepted arc',
      '55° = (1/2) × arc',
      'arc = 110°',
      'Answer: 110°'
    ]
  },
  {
    id: 12, part: 'I', points: 2,
    topic: 'Perpendicular Lines', standard: 'G.GPE.B.5',
    question: 'Line m has slope 4/5. What is the slope of a line perpendicular to m?',
    options: ['−5/4', '4/5', '5/4', '−4/5'],
    correctAnswer: 0,
    solution: [
      'Perpendicular slopes are negative reciprocals.',
      'Perpendicular slope = −5/4',
      'Answer: −5/4'
    ]
  },
  {
    id: 13, part: 'I', points: 2,
    topic: 'Triangle Congruence', standard: 'G.CO.B.8',
    question: 'Two triangles have two pairs of congruent sides and one pair of congruent angles. The angle is NOT between the two sides. Can you conclude the triangles are congruent?',
    options: ['No, SSA is not valid', 'Yes, by SAS', 'Yes, by SSS', 'Yes, by ASA'],
    correctAnswer: 0,
    solution: [
      'SSA (Side-Side-Angle) is NOT a valid congruence criterion.',
      'The angle must be INCLUDED (between the two sides) for SAS.',
      'Answer: No, SSA is not valid'
    ]
  },
  {
    id: 14, part: 'I', points: 2,
    topic: 'Complementary Angles', standard: 'G.CO.C.9',
    question: 'Two angles are complementary. One angle is 3 times the other. What is the measure of the larger angle?',
    options: ['67.5°', '22.5°', '45°', '90°'],
    correctAnswer: 0,
    solution: [
      'Complementary angles sum to 90°.',
      'Let smaller = x, larger = 3x',
      'x + 3x = 90°',
      '4x = 90°',
      'x = 22.5°',
      'Larger = 3(22.5°) = 67.5°',
      'Answer: 67.5°'
    ]
  },
  {
    id: 15, part: 'I', points: 2,
    topic: 'Surface Area — Sphere', standard: 'G.GMD.A.3',
    question: 'A sphere has radius 5. What is its surface area?',
    options: ['100π', '50π', '25π', '500π/3'],
    correctAnswer: 0,
    solution: [
      'Surface area of sphere = 4πr²',
      'SA = 4π(5)² = 4π(25) = 100π',
      'Answer: 100π'
    ]
  },
  {
    id: 16, part: 'I', points: 2,
    topic: 'Special Right Triangles', standard: 'G.SRT.C.8',
    question: 'In a 30-60-90 triangle, the longer leg is 9√3. What is the shorter leg?',
    options: ['9', '18', '9√2', '27'],
    correctAnswer: 0,
    solution: [
      'In 30-60-90 triangle: sides are 1 : √3 : 2',
      'Longer leg (opposite 60°) = x√3',
      'x√3 = 9√3',
      'x = 9 (shorter leg)',
      'Answer: 9'
    ]
  },
  {
    id: 17, part: 'I', points: 2,
    topic: 'Midpoint', standard: 'G.GPE.B.6',
    question: 'What is the midpoint of segment with endpoints (−4, 7) and (6, −3)?',
    options: ['(1, 2)', '(2, 4)', '(5, 2)', '(1, 5)'],
    correctAnswer: 0,
    solution: [
      'Midpoint = ((x₁+x₂)/2, (y₁+y₂)/2)',
      'M = ((−4+6)/2, (7−3)/2)',
      'M = (2/2, 4/2) = (1, 2)',
      'Answer: (1, 2)'
    ]
  },
  {
    id: 18, part: 'I', points: 2,
    topic: 'Similarity', standard: 'G.SRT.A.2',
    question: 'Two similar triangles have corresponding sides 6 and 15. What is the ratio of their perimeters?',
    options: ['2:5', '6:15', '36:225', '12:30'],
    correctAnswer: 0,
    solution: [
      'Ratio of perimeters = ratio of corresponding sides',
      'Side ratio = 6:15 = 2:5',
      'Perimeter ratio = 2:5',
      'Answer: 2:5'
    ]
  },
  {
    id: 19, part: 'I', points: 2,
    topic: 'Arc Length', standard: 'G.C.B.5',
    question: 'In a circle with radius 12, a central angle of 60° intercepts an arc. What is the arc length?',
    options: ['4π', '8π', '2π', '12π'],
    correctAnswer: 0,
    solution: [
      'Arc length = (θ/360°) × 2πr',
      'Arc = (60°/360°) × 2π(12)',
      'Arc = (1/6) × 24π = 4π',
      'Answer: 4π'
    ]
  },
  {
    id: 20, part: 'I', points: 2,
    topic: 'Circle Equation', standard: 'G.GPE.A.1',
    question: 'What is the radius of the circle (x−2)² + (y+5)² = 49?',
    options: ['7', '49', '14', '√49'],
    correctAnswer: 0,
    solution: [
      'Standard form: (x−h)² + (y−k)² = r²',
      'r² = 49',
      'r = 7',
      'Answer: 7'
    ]
  },
  {
    id: 21, part: 'I', points: 2,
    topic: 'Right Triangle — Leg Ratio', standard: 'G.SRT.B.5',
    question: 'In a right triangle, the altitude to the hypotenuse creates two segments of lengths 5 and 20. What is the length of the altitude?',
    options: ['10', '25', '15', '√100'],
    correctAnswer: 0,
    solution: [
      'Geometric Mean Theorem: altitude² = product of segments',
      'altitude² = 5 × 20 = 100',
      'altitude = 10',
      'Answer: 10'
    ]
  },
  {
    id: 22, part: 'I', points: 2,
    topic: 'Glide Reflection', standard: 'G.CO.A.5',
    question: 'A point is reflected over the x-axis, then translated 2 units left. Point (5, 3) becomes what point?',
    options: ['(3, −3)', '(7, −3)', '(3, 3)', '(5, −1)'],
    correctAnswer: 0,
    solution: [
      'Reflection over x-axis: (5, 3) → (5, −3)',
      'Translation 2 left: (5, −3) → (3, −3)',
      'Answer: (3, −3)'
    ]
  },
  {
    id: 23, part: 'I', points: 2,
    topic: 'Regular Polygon', standard: 'G.CO.C.10',
    question: 'What is the measure of each exterior angle of a regular pentagon?',
    options: ['72°', '108°', '36°', '60°'],
    correctAnswer: 0,
    solution: [
      'Sum of exterior angles of any polygon = 360°',
      'Each exterior angle = 360° ÷ n',
      'For pentagon (n=5): 360° ÷ 5 = 72°',
      'Answer: 72°'
    ]
  },
  {
    id: 24, part: 'I', points: 2,
    topic: 'Cross-Section', standard: 'G.GMD.B.4',
    question: 'A plane cuts through a cylinder parallel to its base. What is the cross-section?',
    options: ['Circle', 'Rectangle', 'Ellipse', 'Triangle'],
    correctAnswer: 0,
    solution: [
      'A plane parallel to the base of a cylinder creates a circular cross-section.',
      'Answer: Circle'
    ]
  }
];

// ─── TEST 3 — PART II (7 questions, 2 pts each = 14 pts) ─────────────────────
export const test3PartII: RegentsTestQuestion[] = [
  {
    id: 25, part: 'II', points: 2,
    topic: 'Transformations', standard: 'G.CO.A.4',
    question: 'Triangle PQR has vertices P(−1, 4), Q(2, 6), and R(3, 2). Find the coordinates after a reflection over the line y = x.',
    hint: 'Reflection over y = x rule: (x, y) → (y, x)',
    solution: [
      'Reflection over y = x: swap x and y coordinates',
      'P(−1, 4) → P\'(4, −1)',
      'Q(2, 6) → Q\'(6, 2)',
      'R(3, 2) → R\'(2, 3)',
      'Image vertices: P\'(4, −1), Q\'(6, 2), R\'(2, 3)'
    ],
    rubric: [
      '2 points: All three coordinates correct.',
      '1 point: Two coordinates correct.',
      '0 points: Fewer than two correct or no work shown.'
    ]
  },
  {
    id: 26, part: 'II', points: 2,
    topic: 'Pythagorean Theorem', standard: 'G.SRT.B.4',
    question: 'A television screen is 48 inches wide and 27 inches tall. What is the diagonal measurement (screen size)?',
    hint: 'The diagonal is the hypotenuse of a right triangle.',
    solution: [
      'Use Pythagorean Theorem: c² = a² + b²',
      'c² = 48² + 27²',
      'c² = 2304 + 729 = 3033',
      'c = √3033 ≈ 55.07',
      'The diagonal is approximately 55 inches.'
    ],
    rubric: [
      '2 points: Correct setup and answer ≈55 inches.',
      '1 point: Correct equation but arithmetic error.',
      '0 points: Wrong formula or no work.'
    ]
  },
  {
    id: 27, part: 'II', points: 2,
    topic: 'Supplementary Angles', standard: 'G.CO.C.9',
    question: 'Two angles are supplementary. One angle is 4 times the other. Find the measure of each angle.',
    hint: 'Supplementary angles sum to 180°.',
    solution: [
      'Let smaller angle = x, larger angle = 4x',
      'x + 4x = 180°',
      '5x = 180°',
      'x = 36°',
      'Smaller angle = 36°',
      'Larger angle = 4(36°) = 144°'
    ],
    rubric: [
      '2 points: Both angles correct (36° and 144°).',
      '1 point: Correct equation but arithmetic error.',
      '0 points: Wrong relationship or no work.'
    ]
  },
  {
    id: 28, part: 'II', points: 2,
    topic: 'Perimeter on Coordinate Plane', standard: 'G.GPE.B.7',
    question: 'Rectangle ABCD has vertices A(1, 1), B(7, 1), C(7, 4), and D(1, 4). Find the perimeter.',
    hint: 'Find the length and width, then use P = 2(l + w).',
    solution: [
      'Length AB = |7 − 1| = 6 units',
      'Width BC = |4 − 1| = 3 units',
      'Perimeter = 2(length + width)',
      'P = 2(6 + 3) = 2(9) = 18 units'
    ],
    rubric: [
      '2 points: Correct length, width, and perimeter 18.',
      '1 point: Correct dimensions but formula error.',
      '0 points: Wrong approach or no work.'
    ]
  },
  {
    id: 29, part: 'II', points: 2,
    topic: 'Circle — Secant', standard: 'G.C.A.2',
    question: 'Two chords AB and CD intersect inside circle O at point E. If AE = 6, EB = 8, and CE = 4, find ED.',
    hint: 'Use the intersecting chords theorem: AE × EB = CE × ED',
    solution: [
      'Intersecting Chords Theorem: AE × EB = CE × ED',
      '6 × 8 = 4 × ED',
      '48 = 4 × ED',
      'ED = 48 ÷ 4 = 12',
      'ED = 12 units'
    ],
    rubric: [
      '2 points: Correct theorem and answer ED = 12.',
      '1 point: Correct theorem but arithmetic error.',
      '0 points: Theorem not applied.'
    ]
  },
  {
    id: 30, part: 'II', points: 2,
    topic: 'Volume — Prism', standard: 'G.GMD.A.3',
    question: 'A triangular prism has a triangular base with base 10 cm and height 6 cm. The prism height is 15 cm. Find the volume.',
    hint: 'Volume of prism = base area × height',
    solution: [
      'Area of triangular base = (1/2) × base × height',
      'Base area = (1/2) × 10 × 6 = 30 cm²',
      'Volume = base area × prism height',
      'V = 30 × 15 = 450 cm³'
    ],
    rubric: [
      '2 points: Correct base area and volume 450 cm³.',
      '1 point: Correct approach but arithmetic error.',
      '0 points: Wrong formula or no work.'
    ]
  },
  {
    id: 31, part: 'II', points: 2,
    topic: 'Slope & Distance', standard: 'G.GPE.B.6',
    question: 'Segment EF has endpoints E(−3, 2) and F(5, 8). Find the slope of EF and the length of EF in simplest radical form.',
    hint: 'Use slope formula and distance formula.',
    solution: [
      'Slope = (y₂−y₁)/(x₂−x₁) = (8−2)/(5−(−3)) = 6/8 = 3/4',
      '',
      'Distance = √[(x₂−x₁)² + (y₂−y₁)²]',
      'd = √[(5−(−3))² + (8−2)²]',
      'd = √[8² + 6²] = √[64 + 36] = √100 = 10',
      '',
      'Slope = 3/4, Length = 10'
    ],
    rubric: [
      '2 points: Both slope and length correct.',
      '1 point: One of the two correct.',
      '0 points: Both incorrect or no work.'
    ]
  }
];

// ─── TEST 3 — PART III (3 questions, 4 pts each = 12 pts) ────────────────────
export const test3PartIII: RegentsTestQuestion[] = [
  {
    id: 32, part: 'III', points: 4,
    topic: 'Congruent Triangles Proof', standard: 'G.CO.B.8',
    question: 'Given: Quadrilateral ABCD with diagonals AC and BD intersecting at E. AE ≅ CE and BE ≅ DE. Prove: Triangle ABE ≅ Triangle CDE.',
    diagram: `
       A
       |\\
       | \\
       |  E
       | / \\
       |/   \\
       B     D
        \\   /
         \\ /
          C
    (AE ≅ CE, BE ≅ DE)
`,
    hint: 'Use SAS congruence. Identify the included angle.',
    solution: [
      'PROOF:',
      '',
      'Statement 1: AE ≅ CE',
      'Reason 1: Given',
      '',
      'Statement 2: BE ≅ DE',
      'Reason 2: Given',
      '',
      'Statement 3: ∠AEB ≅ ∠CED',
      'Reason 3: Vertical angles are congruent',
      '',
      'Statement 4: △ABE ≅ △CDE',
      'Reason 4: SAS (Side-Angle-Side) — two sides and the included angle',
      '',
      'Conclusion: Triangle ABE is congruent to triangle CDE. ∎'
    ],
    rubric: [
      '4 points: All statements with correct reasons, SAS identified.',
      '3 points: Most statements correct, congruence criterion stated.',
      '2 points: Two pairs of congruent parts identified.',
      '1 point: Some correct work but incomplete proof.',
      '0 points: No valid proof structure.'
    ]
  },
  {
    id: 33, part: 'III', points: 4,
    topic: 'Trigonometry Application', standard: 'G.SRT.C.8',
    question: 'From the top of a 50-meter tall building, the angle of depression to a car on the ground is 35°. How far is the car from the base of the building? Round to the nearest meter.',
    diagram: `
    Building
    |‾‾‾‾‾‾|
    |      | 50 m
    |      |
    |______|
       \\35°
        \\
         \\
          * Car
       distance = ?
`,
    hint: 'Angle of depression from top = angle of elevation from bottom. Use tangent.',
    solution: [
      'The angle of depression from the top equals the angle of elevation from the car.',
      'Angle of elevation = 35°',
      'Set up right triangle: opposite = 50 m (height), adjacent = distance',
      'tan(35°) = opposite/adjacent = 50/distance',
      'distance = 50/tan(35°)',
      'tan(35°) ≈ 0.7002',
      'distance ≈ 50/0.7002',
      'distance ≈ 71.4',
      'distance ≈ 71 meters',
      'The car is approximately 71 meters from the building.'
    ],
    rubric: [
      '4 points: Correct angle identification, trig function, and answer 71 m.',
      '3 points: Correct function and setup, minor rounding error.',
      '2 points: Correct function but setup error.',
      '1 point: Some correct work but wrong function.',
      '0 points: No valid approach.'
    ]
  },
  {
    id: 34, part: 'III', points: 4,
    topic: 'Coordinate Geometry — Rhombus', standard: 'G.GPE.B.4',
    question: 'Quadrilateral JKLM has vertices J(0, 0), K(5, 0), L(7, 4), M(2, 4). Prove that JKLM is a parallelogram. Then determine if it is a rhombus by checking if all sides are equal.',
    diagram: `
    y
  4 | M(2,4)────────L(7,4)
  3 |  /              /
  2 | /              /
  1 |/              /
  0 |J(0,0)────────K(5,0)
    +──────────────────── x
    0  1  2  3  4  5  6  7
`,
    hint: 'Show opposite sides parallel (slopes). Check all four side lengths.',
    solution: [
      'STEP 1: Prove parallelogram (opposite sides parallel)',
      '',
      'Slope JK = (0−0)/(5−0) = 0',
      'Slope ML = (4−4)/(7−2) = 0',
      'JK ∥ ML ✓',
      '',
      'Slope JM = (4−0)/(2−0) = 4/2 = 2',
      'Slope KL = (4−0)/(7−5) = 4/2 = 2',
      'JM ∥ KL ✓',
      '',
      'Both pairs of opposite sides parallel → JKLM is a parallelogram.',
      '',
      'STEP 2: Check if rhombus (all sides equal)',
      '',
      'JK = √[(5−0)² + (0−0)²] = √25 = 5',
      'ML = √[(7−2)² + (4−4)²] = √25 = 5',
      'JM = √[(2−0)² + (4−0)²] = √[4+16] = √20 = 2√5',
      'KL = √[(7−5)² + (4−0)²] = √[4+16] = √20 = 2√5',
      '',
      'JK = ML = 5, but JM = KL = 2√5 ≈ 4.47',
      '5 ≠ 2√5, so not all sides equal → NOT a rhombus.',
      '',
      'Conclusion: JKLM is a parallelogram but NOT a rhombus. ∎'
    ],
    rubric: [
      '4 points: Parallelogram proven with slopes, all four side lengths calculated, correct conclusion.',
      '3 points: Parallelogram proven, rhombus check incomplete.',
      '2 points: Two slopes correct and one pair parallel shown.',
      '1 point: Some correct calculations but no valid conclusion.',
      '0 points: No valid coordinate geometry work.'
    ]
  }
];

// ─── TEST 3 — PART IV (1 question, 6 pts) ────────────────────────────────────
export const test3PartIV: RegentsTestQuestion[] = [
  {
    id: 35, part: 'IV', points: 6,
    topic: 'Real-World Modeling — Composite Solids', standard: 'G.MG.A.1',
    question: `A decorative candle consists of a cylinder with a hemisphere (half-sphere) on top. The cylinder has radius 4 inches and height 10 inches. The hemisphere has the same radius of 4 inches.

(a) Find the total volume of the candle (cylinder + hemisphere). Leave your answer in terms of π.

(b) If wax costs $0.15 per cubic inch, what is the cost to make one candle? Round to the nearest cent.

(c) The entire outer surface (lateral surface of cylinder + curved surface of hemisphere, NOT the base) is to be decorated with glitter. Find the total surface area to be decorated. Leave your answer in terms of π.`,
    diagram: `
       ___
      /   \\
     |     |  ← Hemisphere (r=4)
     |_____|
     |     |
     |     |  ← Cylinder (r=4, h=10)
     |     |
     |_____|
      base
`,
    hint: 'Volume: cylinder = πr²h, hemisphere = (2/3)πr³. Surface: cylinder lateral = 2πrh, hemisphere curved = 2πr²',
    solution: [
      '═══ PART (a): Total Volume ═══',
      '',
      'Volume of cylinder = πr²h = π(4)²(10) = π(16)(10) = 160π in³',
      '',
      'Volume of hemisphere = (2/3)πr³ = (2/3)π(4)³ = (2/3)π(64) = (128/3)π in³',
      '',
      'Total volume = 160π + (128/3)π',
      '= (480/3)π + (128/3)π',
      '= (608/3)π in³',
      '',
      'Total volume = (608/3)π in³ ≈ 202.67π in³',
      '',
      '═══ PART (b): Cost of Wax ═══',
      '',
      'Volume in decimal: (608/3)π ≈ 202.67 × 3.14159 ≈ 636.65 in³',
      'Cost = volume × price per cubic inch',
      'Cost = 636.65 × $0.15',
      'Cost ≈ $95.50',
      '',
      'The cost to make one candle is approximately $95.50.',
      '',
      '═══ PART (c): Decorated Surface Area ═══',
      '',
      'Lateral SA of cylinder = 2πrh = 2π(4)(10) = 80π in²',
      '',
      'Curved SA of hemisphere = 2πr² = 2π(4)² = 2π(16) = 32π in²',
      '',
      'Total decorated area = 80π + 32π = 112π in²',
      '',
      'Total decorated surface area = 112π in²'
    ],
    rubric: [
      '6 points: All three parts fully correct with work shown.',
      '5 points: Two parts fully correct, third has minor error.',
      '4 points: Parts (a) and (c) correct, part (b) has error.',
      '3 points: Two of three parts correct.',
      '2 points: One part fully correct.',
      '1 point: Correct formulas but multiple errors.',
      '0 points: No valid work.'
    ],
    commonMistakes: [
      'Using full sphere volume (4/3)πr³ instead of hemisphere (2/3)πr³.',
      'Forgetting to convert volume to decimal before multiplying by cost.',
      'Including the base circle in surface area (problem says NOT the base).',
      'Using diameter (8) instead of radius (4).',
      'Not leaving answers in terms of π when instructed.'
    ]
  }
];